import { z } from 'zod';

/**
 * Prompts, KB y dossier por empresa (§4.3 del plan). Cada guardado crea una versión; se edita un
 * borrador y se publica; rollback = volver a publicar una versión anterior. Publicar empuja el
 * contenido a `leads.prompts` del stack (n8n lo lee con cache de 60 s) si la escritura hacia el
 * stack está habilitada.
 */

const isoDate = z.string();

/**
 * Nombres que existen en `leads.prompts` de los stacks (CHECK `prompts_name_check`): son los que
 * el panel puede editar y publicar.
 */
export const EDITABLE_PROMPT_NAMES = [
  'extractor',
  'qualifier',
  'personalizer',
  'first_contact',
] as const;
/**
 * Todos los textos que la plataforma conoce. `answerer` (el asistente de respuestas del bot de
 * Star Sam y Sweet Papaya) vive desde el 2026-09-23 en `leads.prompts` como fila `bot_answerer`
 * (ver `LEGACY_PROMPT_ROWS`) y es editable donde esa fila existe. No está en
 * `EDITABLE_PROMPT_NAMES` porque ésos son los que se ofrecen a una empresa nueva, y una empresa
 * sin bot no tiene dónde usarlo.
 */
export const PROMPT_NAMES = [
  ...EDITABLE_PROMPT_NAMES,
  'answerer',
  /**
   * Instrucciones extra que recibe el asistente en la PRIMERA respuesta con dossier (modo
   * «Respuesta del asistente + dossier», hoy Star Sam). Fila `first_contact_instructions`
   * (migración 031, 2026-09-24). Como `answerer`: sólo existe donde hay bot.
   */
  'first_reply_instructions',
] as const;
export const promptNameSchema = z.enum(PROMPT_NAMES);
export type PromptName = z.infer<typeof promptNameSchema>;

/**
 * Nombre de la fila en `<schema>.prompts` del stack cuando difiere del nombre en la plataforma.
 * El resto se llama igual en los dos lados.
 */
export const LEGACY_PROMPT_ROWS: Readonly<Partial<Record<PromptName, string>>> = {
  answerer: 'bot_answerer',
  first_reply_instructions: 'first_contact_instructions',
};

/** Nombre de la fila del stack para un prompt de la plataforma. */
export function legacyPromptRow(name: PromptName): string {
  return LEGACY_PROMPT_ROWS[name] ?? name;
}

/** Prompt de la plataforma para una fila del stack (`null` si la plataforma no lo conoce). */
export function promptNameFromLegacyRow(row: string): PromptName | null {
  for (const name of PROMPT_NAMES) if (legacyPromptRow(name) === row) return name;
  return null;
}

export const PROMPT_LABELS: Record<PromptName, { label: string; description: string }> = {
  extractor: {
    label: 'Lectura de la web',
    description:
      'Lee la web de cada tienda y saca sus datos: qué vende, cuántos productos tiene, si vende online…',
  },
  qualifier: {
    label: 'Criterios para calificar tiendas',
    description:
      'Decide si una tienda encaja con tu negocio y le pone una puntuación. Conserva el formato de respuesta que ya tiene.',
  },
  personalizer: {
    label: 'Personalización del primer email',
    description: 'Escribe la primera frase del email, personalizada para cada tienda.',
  },
  first_contact: {
    label: 'Respuesta de primer contacto con dossier',
    description:
      'El email que se envía, con el dossier adjunto, cuando alguien responde pidiendo información.',
  },
  answerer: {
    label: 'Asistente de respuestas',
    description:
      'Responde los emails de los contactos usando solo la base de conocimiento; si no está seguro, te lo pasa a ti.',
  },
  first_reply_instructions: {
    label: 'Instrucciones de la primera respuesta con dossier',
    description:
      'Lo que el asistente tiene en cuenta, además de sus reglas de siempre, cuando contesta por primera vez a alguien interesado y adjunta el dossier (por ejemplo: mencionar el adjunto, no hablar de precios).',
  },
};

/** De dónde viene el texto que usa el motor (lo decide la importación desde n8n). */
export const PROMPT_SOURCE_KINDS = ['legacy_db', 'shared', 'n8n_code'] as const;
export type PromptSourceKind = (typeof PROMPT_SOURCE_KINDS)[number];

/** Quién originó una versión: el panel, la importación inicial o un cambio hecho fuera del panel. */
export const PROMPT_ORIGINS = ['panel', 'import', 'external'] as const;
export type PromptOrigin = (typeof PROMPT_ORIGINS)[number];

/** Límite duro de tamaño de un prompt (bytes UTF-8). */
export const PROMPT_MAX_BYTES = 64 * 1024;

export const PROMPT_STATUSES = ['draft', 'published', 'archived'] as const;
export const promptStatusSchema = z.enum(PROMPT_STATUSES);

export const LEGACY_PUSH_STATUSES = ['pending', 'ok', 'failed', 'skipped'] as const;

export const promptVersionSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  name: promptNameSchema,
  version: z.number().int(),
  status: promptStatusSchema,
  content: z.string(),
  note: z.string().nullable(),
  authorEmail: z.string().nullable(),
  createdAt: isoDate,
  publishedAt: isoDate.nullable(),
  publishedByEmail: z.string().nullable(),
  previewedAt: isoDate.nullable(),
  legacyPushStatus: z.enum(LEGACY_PUSH_STATUSES).nullable(),
  legacyPushError: z.string().nullable(),
  origin: z.enum(PROMPT_ORIGINS),
  /** Dónde vive el texto en el motor (p. ej. "n8n · 10 - Inbound Bot (Sweet Papaya) › Answer"). */
  sourceRef: z.string().nullable(),
  /** Fecha y autor que tenía el texto en el motor cuando se importó (metadata del stack). */
  externalUpdatedAt: isoDate.nullable(),
  externalUpdatedBy: z.string().nullable(),
});
export type PromptVersion = z.infer<typeof promptVersionSchema>;

/** Resumen por nombre de prompt, sin contenido de las versiones viejas. */
export const promptSummarySchema = z.object({
  name: promptNameSchema,
  label: z.string(),
  description: z.string(),
  published: promptVersionSchema.nullable(),
  draft: promptVersionSchema.nullable(),
  versions: z.number(),
  /**
   * `false` si el texto vive en el código del flujo de n8n o es compartido con otra empresa: el
   * panel lo muestra (y guarda su historial) pero no lo puede publicar.
   */
  editable: z.boolean(),
  /** Explicación en lenguaje llano de dónde vive el texto cuando no es editable. */
  readOnlyReason: z.string().nullable(),
});
export type PromptSummary = z.infer<typeof promptSummarySchema>;

export const promptsResponseSchema = z.object({
  tenantId: z.uuid(),
  canWrite: z.boolean(),
  requirePreview: z.boolean(),
  legacyWritesEnabled: z.boolean(),
  prompts: z.array(promptSummarySchema),
});
export type PromptsResponse = z.infer<typeof promptsResponseSchema>;

export const saveDraftSchema = z.object({
  content: z.string().min(1),
  note: z.string().trim().max(500).optional(),
  /** Versión sobre la que se editó (para avisar si alguien publicó otra mientras tanto). */
  baseVersionId: z.uuid().nullable().optional(),
});
export type SaveDraft = z.infer<typeof saveDraftSchema>;

export const publishPromptSchema = z.object({
  /** Versión publicada que la persona tenía a la vista (`null` si no había). Chequeo optimista. */
  expectedPublishedId: z.uuid().nullable(),
  /** Publicar aunque el contenido del stack haya sido cambiado por fuera (WF09). */
  force: z.boolean().default(false),
});
export type PublishPrompt = z.input<typeof publishPromptSchema>;

export const publishResponseSchema = z.object({
  version: promptVersionSchema,
  legacy: z.object({
    status: z.enum(LEGACY_PUSH_STATUSES),
    message: z.string().nullable(),
  }),
});
export type PublishResponse = z.infer<typeof publishResponseSchema>;

export const previewResponseSchema = z.object({
  status: z.enum(['ok', 'failed', 'unavailable']),
  message: z.string(),
  previewedAt: isoDate,
  /** Resultado lado a lado (cuando haya runner): publicada vs borrador. */
  samples: z.array(
    z.object({
      input: z.string(),
      published: z.string().nullable(),
      candidate: z.string().nullable(),
    }),
  ),
});
export type PreviewResponse = z.infer<typeof previewResponseSchema>;

export const legacyPromptSchema = z.object({
  name: z.string(),
  content: z.string(),
  updatedAt: isoDate.nullable(),
  updatedBy: z.string().nullable(),
  /** Coincide con la versión publicada en la plataforma. */
  inSync: z.boolean(),
});
export const legacyPromptsResponseSchema = z.object({
  available: z.boolean(),
  message: z.string().nullable(),
  prompts: z.array(legacyPromptSchema),
});
export type LegacyPromptsResponse = z.infer<typeof legacyPromptsResponseSchema>;

// ------------------------------------------------------------------ KB (lectura)

export const kbQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

export const kbChunkSchema = z.object({
  id: z.string(),
  source: z.string(),
  text: z.string(),
  createdAt: isoDate.nullable(),
  /** Apartado del documento al que pertenece (el último `##` anterior). */
  section: z.string(),
  /** Título del fragmento (su propio encabezado, o el apartado si es una continuación). */
  title: z.string(),
  /** Texto sin encabezados ni marcas de Markdown, recortado. */
  preview: z.string(),
  /** Orden dentro de la KB (migración 031); `null` en una base sin esa columna. */
  position: z.number().int().nullable().default(null),
});
export const kbSectionSchema = z.object({ title: z.string(), chunks: z.number() });
export const kbResponseSchema = z.object({
  available: z.boolean(),
  message: z.string().nullable(),
  /**
   * ¿Se puede editar desde el panel? Hace falta: permiso `prompts:write` en la empresa, escritura
   * hacia el stack habilitada, la migración 031 en su base (apartado/orden + archivo) y el servicio
   * de embeddings configurado. `readOnlyReason` lo explica en lenguaje llano cuando no.
   */
  editable: z.boolean().default(false),
  readOnlyReason: z.string().nullable().default(null),
  total: z.number(),
  sources: z.array(z.object({ source: z.string(), chunks: z.number() })),
  /** Apartados en el orden del documento, con cuántos fragmentos tiene cada uno (sin filtrar). */
  sections: z.array(kbSectionSchema),
  /** Última vez que se cargó o cambió algún fragmento. */
  lastUpdatedAt: isoDate.nullable(),
  chunks: z.array(kbChunkSchema),
});
export type KbResponse = z.infer<typeof kbResponseSchema>;

// ------------------------------------------------------------------ KB (edición, 2026-09-24)

/** Tamaño máximo de un fragmento (los de la carga inicial rondan los 700 caracteres). */
export const KB_CHUNK_MAX_CHARS = 2000;
export const KB_CHUNK_MIN_CHARS = 20;
export const KB_SECTION_MAX_CHARS = 120;

const kbText = z
  .string()
  .trim()
  .min(KB_CHUNK_MIN_CHARS, { error: `Escribe al menos ${String(KB_CHUNK_MIN_CHARS)} caracteres` })
  .max(KB_CHUNK_MAX_CHARS, {
    error: `Un fragmento no puede pasar de ${String(KB_CHUNK_MAX_CHARS)} caracteres: pártelo en dos`,
  });
const kbSection = z.string().trim().min(1).max(KB_SECTION_MAX_CHARS);

export const kbChunkCreateSchema = z.object({ section: kbSection, text: kbText });
export type KbChunkCreate = z.infer<typeof kbChunkCreateSchema>;

export const kbChunkUpdateSchema = z.object({
  text: kbText,
  section: kbSection.optional(),
  /** El texto que la persona tenía a la vista (chequeo optimista). */
  expectedText: z.string(),
});
export type KbChunkUpdate = z.infer<typeof kbChunkUpdateSchema>;

export const kbChunkDeleteSchema = z.object({ expectedText: z.string() });
export type KbChunkDelete = z.infer<typeof kbChunkDeleteSchema>;

export const kbChunkParamsSchema = z.object({
  tenantId: z.uuid(),
  chunkId: z.string().regex(/^[0-9A-Za-z-]{1,64}$/),
});

export const KB_REVISION_ACTIONS = ['create', 'update', 'delete'] as const;
export const kbRevisionSchema = z.object({
  id: z.uuid(),
  chunkId: z.string(),
  action: z.enum(KB_REVISION_ACTIONS),
  sectionBefore: z.string().nullable(),
  textBefore: z.string().nullable(),
  sectionAfter: z.string().nullable(),
  textAfter: z.string().nullable(),
  actorEmail: z.string().nullable(),
  createdAt: isoDate,
});
export type KbRevision = z.infer<typeof kbRevisionSchema>;
export const kbRevisionsResponseSchema = z.object({ revisions: z.array(kbRevisionSchema) });
export type KbRevisionsResponse = z.infer<typeof kbRevisionsResponseSchema>;

export const kbWriteResponseSchema = z.object({
  chunk: kbChunkSchema.nullable(),
  revision: kbRevisionSchema,
});
export type KbWriteResponse = z.infer<typeof kbWriteResponseSchema>;

// ------------------------------------------------------------------ controles del bot (2026-09-24)

/**
 * La llave «Responder la primera vez con el dossier adjunto» de la empresa. Vive en la base del
 * stack (`leads.bot_settings.first_contact_enabled`, migración 031) y el asistente la lee en cada
 * respuesta: cambiarla no requiere reiniciar nada.
 */
export const botSettingsSchema = z.object({
  /** `false` = esta empresa no tiene esa llave (sin bot, o sin la migración 031). */
  available: z.boolean(),
  message: z.string().nullable(),
  /** Valor guardado en la base. `null` = sin decidir: manda la configuración del motor. */
  firstContactEnabled: z.boolean().nullable(),
  /** Lo que hace el asistente hoy (base > configuración del motor informada por el sync). */
  effective: z.boolean().nullable(),
  /** Cómo contesta ese primer contacto (del latido del sync del dossier). */
  mode: z.enum(['template', 'bot_answer']).nullable(),
  updatedAt: isoDate.nullable(),
  updatedBy: z.string().nullable(),
  canWrite: z.boolean(),
});
export type BotSettings = z.infer<typeof botSettingsSchema>;

export const botSettingsUpdateSchema = z.object({
  firstContactEnabled: z.boolean(),
  /** Valor que la persona tenía a la vista (chequeo optimista); `null` = sin decidir. */
  expected: z.boolean().nullable(),
});
export type BotSettingsUpdate = z.infer<typeof botSettingsUpdateSchema>;

// ------------------------------------------------------------------ Dossier / documentos

export const documentKeySchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,62}$/, { error: 'Sólo minúsculas, números y guiones' });

export const documentVersionSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  active: z.boolean(),
  attachmentId: z.uuid(),
  filename: z.string(),
  mime: z.string(),
  sizeBytes: z.number(),
  sha256: z.string(),
  note: z.string().nullable(),
  uploadedByEmail: z.string().nullable(),
  createdAt: isoDate,
  /** Veces que este archivo exacto salió adjunto en un envío. */
  timesSent: z.number(),
});
export type DocumentVersion = z.infer<typeof documentVersionSchema>;

export const documentSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  title: z.string(),
  versions: z.array(documentVersionSchema),
});
export type TenantDocument = z.infer<typeof documentSchema>;

export const sentAttachmentSchema = z.object({
  id: z.uuid(),
  filename: z.string(),
  mime: z.string(),
  sizeBytes: z.number(),
  sha256: z.string(),
  createdAt: isoDate,
  timesSent: z.number(),
  lastSentAt: isoDate.nullable(),
});

/**
 * Qué hace hoy el asistente (bot de n8n) con el dossier, según el último latido del sync del
 * dossier (`bot.dossier-sync`). `null` = el asistente de esta empresa nunca informó nada.
 */
export const dossierBotUsageSchema = z.object({
  /** ¿Está activada la respuesta de primer contacto con dossier en el asistente? */
  firstContactEnabled: z.boolean(),
  /**
   * Cómo contesta ese primer contacto: `template` (texto fijo + dossier), `bot_answer`
   * («Respuesta del asistente + dossier»: la escribe el asistente con la base de conocimiento).
   * `null` = el asistente todavía no lo informó (se trata como `template`).
   */
  firstContactMode: z.enum(['template', 'bot_answer']).nullable(),
  /** Último latido del sync (corre cada hora). */
  reportedAt: isoDate,
  /** `true` si el último latido tiene más de 3 h: el dato puede estar desactualizado. */
  stale: z.boolean(),
  /** El sync terminó con error la última vez (no pudo bajar o guardar la copia). */
  lastSyncFailed: z.boolean(),
  /** Versión y sha de la copia de respaldo que tiene el asistente por si la plataforma no responde. */
  localVersion: z.number().int().nullable(),
  localSha256: z.string().nullable(),
});
export type DossierBotUsage = z.infer<typeof dossierBotUsageSchema>;

export const documentsResponseSchema = z.object({
  documents: z.array(documentSchema),
  /** Archivos que efectivamente salieron adjuntos (ingesta de envíos). */
  sent: z.array(sentAttachmentSchema),
  /** Uso del dossier por el asistente (ver `dossierBotUsageSchema`). */
  dossierBot: dossierBotUsageSchema.nullable(),
});
export type DocumentsResponse = z.infer<typeof documentsResponseSchema>;

/** Tipos permitidos para el dossier y otros documentos de la empresa. */
export const DOCUMENT_MIMES = ['application/pdf', 'image/png', 'image/jpeg'] as const;
export const DOCUMENT_MAX_BYTES = 15 * 1024 * 1024;

export const uploadDocumentSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  filename: z.string().trim().min(1).max(255),
  mime: z.enum(DOCUMENT_MIMES),
  /** Contenido en base64 (el mismo formato que la ingesta de envíos). */
  contentBase64: z.string().min(4),
  note: z.string().trim().max(500).optional(),
});
export type UploadDocument = z.infer<typeof uploadDocumentSchema>;
